import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { filter, map, shareReplay, takeUntil } from 'rxjs/operators';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { BehaviorSubject } from 'rxjs';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { format } from 'date-fns';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
export type CalendarModes = 'occupancy' | 'tasks';

export interface Property {
  pricingAndAvailabilityLoading?: boolean;
  id: string;
  name: string;
  picture: string;
  currency: string;
  counts: {
    airbnb: number;
    homeaway: number;
    booking: number;
    total: number;
  };

  events?: Event[];
  tasks?: any[];
  calendar_restricted?: boolean;
}

export interface Event {
  end_date: string;
  status: string;
  guest_picture: string;
  guest: string;
  platform: string;
  start_date: string;
}

export interface CalendarCellUpdate {
  price?: {
    amount: number;
    currency: string;
  };
  note?: string;
  closedForCheckin?: boolean;
  closedForCheckout?: boolean;
  locked?: boolean;
  available?: boolean;
  min_stay?: number;
  closed_for_checkin?: boolean;
  closed_for_checkout?: boolean;
  lastCallFailed?: boolean;
}

/**
 * Localized service to manage calendar state
 */
@Injectable({
  providedIn: 'any',
})
export class CalendarStateService extends OnDestroyComponent {
  public featureFlagOptimistic = false;
  public calendarPermissions$ = this.userPermissionsService.calendarPermissions$;
  /**
   * The URL is the source of truth for the current mode.
   * Use setMode to update the URL and this observable will update accordingly.
   */
  public mode$ = this.route.paramMap.pipe(
    filter((params) => params.has('mode')),
    map((params) => params.get('mode') as CalendarModes),
    shareReplay(1)
  );

  public properties$ = new BehaviorSubject<Property[]>([]);
  public optimisticUpdates: Record<string, CalendarCellUpdate> = {};

  constructor(
    private readonly userPermissionsService: UserPermissionsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly posthogService: PosthogService,
    private readonly toastNotifications: ToastNotificationsService,
    private readonly authService: AuthenticationService
  ) {
    super();
    this.authService.user$.pipe(takeUntil(this.destroyed$)).subscribe((user) => {
      if (user) {
        this.posthogService.calendarOptimisticUpdateEnabled$.pipe(takeUntil(this.destroyed$)).subscribe((enabled) => {
          this.featureFlagOptimistic = enabled;
        });
      }
    });
  }

  public setMode = ({ value }: MatButtonToggleChange): void => {
    const newMode = value ?? 'occupancy';
    this.router.navigate([`../${newMode}`], { relativeTo: this.route });
  };

  /** Optimistic handlers */

  public setOptimisticUpdates(
    propertyId: string | number,
    dates: Date[],
    desiredPrice: number,
    desiredAvailability: boolean,
    desiredLengthOfStay: number,
    desiredNote: string,
    desiredClosedForCheckin: boolean,
    desiredClosedForChekout: boolean,
    properties: Property[]
  ) {
    if (!this.featureFlagOptimistic) return;
    dates.forEach((date) => {
      const dateStr = format(date, 'YYYY-MM-DD');
      this.optimisticUpdates[`${propertyId}-${dateStr}`] = {
        price: {
          amount: desiredPrice,
          currency: properties.find((prop) => prop.id === propertyId).currency,
        },
        available: desiredAvailability,
        min_stay: desiredLengthOfStay,
        note: desiredNote,
        closedForCheckin: desiredClosedForCheckin,
        closedForCheckout: desiredClosedForChekout,
        locked: true,
      };
    });
  }

  public clearOptimisticUpdates(propertyId: string | number, dates: Date[]) {
    if (!this.featureFlagOptimistic) return;
    dates.forEach((date) => {
      delete this.optimisticUpdates[`${propertyId}-${format(date, 'YYYY-MM-DD')}`];
    });
  }

  public handleCalendarUpdateError({
    propertyId,
    propertyName,
    dates,
    startDate,
    endDate,
  }: {
    propertyId: string | number;
    propertyName: string;
    dates: Date[];
    startDate: Date;
    endDate: Date;
  }) {
    if (!this.featureFlagOptimistic) return;

    dates.forEach((date) => {
      const dateStr = format(date, 'YYYY-MM-DD');
      this.optimisticUpdates[`${propertyId}-${dateStr}`] = {
        lastCallFailed: true,
      };
    });

    this.toastNotifications.error(
      `Oops, there was an error updating the calendar for ${propertyName} (${format(startDate, 'MMM D')} - ${format(endDate, 'MMM D')}).`
    );
  }
}
